import {
  PageContainer,
  Panel,
  Typography,
  Box,
  Paper,
  Button,
  CloudUploadIcon,
  ControlledInput,
  ControlledCheckbox,
  Tabs,
  DataConnectionIcon,
  DataSettingsIcon,
  SettingsIcon,
  MuiAutocomplete as Autocomplete,
  TextField,
  Grid2 as Grid,
  Alert,
  CopyIcon,
  Tooltip,
} from '@cherre-frontend/ui';
import React, { useState, useEffect } from 'react';
import {
  generatePath,
  useHistory,
  useParams,
  matchPath,
} from 'react-router-dom';
import {
  ModelDetailsRoute,
  TableExtractionTemplateCreateRoute,
  ModelTableReadRoute,
  ModelTableEditRoute,
  ModelTableCreateRoute,
} from '../../routes';
import { useModelTableCreate } from '../../services/dom/useModelTableCreate';
import { useModelTableUpdate } from '../../services/dom/useModelTableUpdate';
import LibraryBreadcrumbs from '../../components/LibraryBreadcrumbs';
import SchemaTable from '../../components/SchemaTable';
import DspMappingTable, {
  emptyMappingRow,
} from '../../components/DspMappingTable';
import DspConfigurationTable from '../../components/DspConfigurationTable';
import { useLibraryModelUpdate } from '../../services/dom/useLibraryModelUpdate';
import { useTargetTableForms } from '../../hooks/useTargetTableForms';
import {
  TabValue,
  TargetTableFormValues,
  TSA_VALUES,
  TableMode,
} from '../../schema';
import { Controller } from 'react-hook-form';
import { capitalize } from 'lodash';
import { useSelector } from 'react-redux';
import { toLowerCaseKabobCaseAlphaNumeric } from '../../utils';
import { $modelTableDetails } from '../../services/dom/useModelTableDetails';
import Header from '../../components/ModelsLibraryHeader';
import TemplatesSectionWrapper from '../../components/TemplatesSection';
import {
  constSelector,
  useCherreValue,
  useMutation,
  CherreState,
} from '@cherre-frontend/data-fetching';
import { $libraryModelDetails } from '../../services/dom/useLibraryModelDetails';
import { useModelTableDetailsQuery$data } from '../../services/dom/__generated__/useModelTableDetailsQuery.graphql';
import { useLibraryModelDetailsQuery$data } from '../../services/dom/__generated__/useLibraryModelDetailsQuery.graphql';
const TargetTableCreate: React.FC = () => {
  const history = useHistory();
  const location = useHistory().location;
  const { pathname } = location;

  const { modelId, tableId } = useParams<{
    modelId: string;
    tableId?: string;
  }>();
  const [createdTable, setCreatedTable] = React.useState<boolean>(false);
  const modelDetails = useCherreValue($libraryModelDetails({ uid: modelId }));
  const updateModel = useLibraryModelUpdate();
  const [tabValue, setTabValue] = useState(TabValue.TableSchema);

  // Add mode state to track current component mode
  const [mode] = useState<TableMode>(() => {
    // Check if the current path matches the edit route

    const isReadPath = matchPath(pathname, {
      path: ModelTableReadRoute.routeDescriptor.path,
      exact: true,
      strict: true,
    });

    const isEditPath = matchPath(pathname, {
      path: ModelTableEditRoute.routeDescriptor.path,
      exact: true,
      strict: true,
    });

    if (isEditPath) {
      return TableMode.Edit;
    } else if (isReadPath) {
      return TableMode.Read;
    } else {
      return TableMode.Create;
    }
  });

  // Load table details if in read or edit mode
  const {
    targetTableDefinition: targetTableDetails,
    targetTableDefinitionsPackage: modelDetailsFromTable,
  } =
    useCherreValue(
      tableId
        ? $modelTableDetails({ modelId, targetTableDefinitionId: tableId })
        : constSelector(null)
    ) ?? {};

  const {
    tableForm,
    mappingForm,
    tsaForm,
    values,
    setTableData,
    setTsaTableData,
  } = useTargetTableForms();

  const orgSlug = useSelector(
    (state) => state.user.profile.value.organizationSlug
  );

  const { dsp, tableData, fieldNameOptions, mappingFieldsData, tsaFieldsData } =
    values;

  const updateModelTables = (tableId) => {
    //TODO: @gaspardetienne97 use uid instead of name
    const targetTableDefinitionNames =
      modelDetails?.targetTableDefinitions?.map((table) => table.name) ?? [];
    updateModel({
      targetTableDefinitionNames: [...targetTableDefinitionNames, tableId],
      targetTableDefinitionsPackageUID: modelDetails?.uid ?? '',
      displayName: modelDetails?.displayName ?? '',
    });
  };

  const createTable = useMutation(useModelTableCreate, {
    onCompleted: async (response, ctx) => {
      await updateModelTables(response.createTargetTableDefinition.name);
      setCreatedTable(true);
      ctx.showSnackbar({
        message: 'Target table created successfully',
        type: 'success',
      });
      const tableId = response.createTargetTableDefinition.uid;
      ctx.recoil.reset(
        $modelTableDetails({
          modelId,
          targetTableDefinitionId: tableId,
        }) as CherreState<useModelTableDetailsQuery$data>
      );

      ctx.recoil.reset(
        $libraryModelDetails({
          uid: modelId,
        }) as CherreState<
          useLibraryModelDetailsQuery$data['targetTableDefinitionsPackage']
        >
      );

      history.push(
        generatePath(ModelTableReadRoute.routeDescriptor.path, {
          modelId,
          tableId,
        })
      );
    },
    onError: (_, ctx) => {
      ctx.showSnackbar({
        message: `Failed to create target table`,
        type: 'error',
      });
    },
  });
  const updateTable = useMutation(useModelTableUpdate, {
    onCompleted: (response, ctx) => {
      ctx.showSnackbar({
        message: 'Target table updated successfully',
        type: 'success',
      });
      ctx.recoil.reset(
        $modelTableDetails({
          modelId,
          targetTableDefinitionId: response.updateTargetTableDefinition.uid,
        }) as CherreState<useModelTableDetailsQuery$data>
      );

      ctx.recoil.reset(
        $libraryModelDetails({
          uid: modelId,
        }) as CherreState<
          useLibraryModelDetailsQuery$data['targetTableDefinitionsPackage']
        >
      );

      history.push(
        generatePath(ModelTableReadRoute.routeDescriptor.path, {
          modelId,
          tableId: response.updateTargetTableDefinition.uid,
        })
      );
    },
    onError: (_, ctx) => {
      ctx.showSnackbar({
        message: `Failed to update target table`,
        type: 'error',
      });
      ctx.recoil.reset(
        $modelTableDetails({
          modelId,
          targetTableDefinitionId: tableId,
        }) as CherreState<useModelTableDetailsQuery$data>
      );
    },
  });

  // Load data from API into forms when available (for read/edit modes)
  useEffect(() => {
    if (targetTableDetails) {
      // Format columns data for the form
      const formattedColumns =
        targetTableDetails.columns?.map((column) => ({
          ...column,
          dsp: column.dsp
            ? {
                ...column.dsp,
                tsa: column.dsp.tsa ? { ...column.dsp.tsa } : null,
                mappingFields:
                  column.dsp.mappingFields?.map((field) => ({
                    ...field,
                    tsa: field.tsa ? { ...field.tsa } : null,
                  })) ?? [],
              }
            : null,
        })) || [];

      // Extract mapping data from columns
      const mappingFields =
        targetTableDetails.columns?.flatMap((column) => {
          if (!column.dsp?.mappingFields?.length) {
            return [];
          }

          return column.dsp.mappingFields.map((mappingField) => ({
            fieldName: column.name,
            descriptionName: mappingField.descriptionName,
            displayName: mappingField.displayName,
            mappingName: mappingField.mappingName,
            required: mappingField.required,
            targetName: mappingField.targetName,
            mappingValue:
              mappingField.mappingValue ?? emptyMappingRow.mappingValue,
            displayPreference:
              (mappingField.displayPreference as typeof emptyMappingRow.displayPreference) ??
              emptyMappingRow.displayPreference,
          }));
        }) || [];

      // Extract TSA data from columns
      const tsaFields =
        targetTableDetails.columns?.map((column) => {
          const tsaValue = column.dsp?.tsa
            ? column.dsp.tsa.required
              ? TSA_VALUES.REQUIRED
              : column.dsp.tsa.enabled
              ? TSA_VALUES.ENABLED
              : TSA_VALUES.DISABLED
            : TSA_VALUES.DISABLED;

          return {
            fieldName: column.name,
            tsa: tsaValue,
          };
        }) || [];

      // Reset forms with data from API

      tableForm.reset({
        tableName:
          (targetTableDetails.displayName ?? targetTableDetails.name) +
          (mode === TableMode.Create && tableId ? ' Copy' : ''),
        description: targetTableDetails.description || '',
        dsp: !!targetTableDetails.dsp,
        submissionType: targetTableDetails.dsp?.submissionType || undefined,
        orgSlug: targetTableDetails.dsp?.orgSlug || undefined,
        tableData: formattedColumns,
      });

      // Reset mapping form
      mappingForm.reset({
        fields: mappingFields.length > 0 ? mappingFields : [emptyMappingRow],
      });

      // Reset TSA form
      tsaForm.reset({
        fields:
          tsaFields.length > 0
            ? tsaFields
            : [
                {
                  fieldName: '',
                  tsa: TSA_VALUES.DISABLED,
                },
              ],
      });
    }
  }, [targetTableDetails, mode]);

  const handleFileUploadClick = (_event: React.MouseEvent) => {
    //TODO: @gaspardetienne97 implement handleFileUploadClick
    throw new Error('Function not implemented.');
  };

  const handleDuplicateTableClick = (_event: React.MouseEvent) => {
    history.push(
      generatePath(ModelTableCreateRoute.routeDescriptor.path, {
        modelId,
        tableId: targetTableDetails?.uid,
      })
    );
  };

  const handleCreateTemplateClick = () => {
    tableId &&
      history.push(
        generatePath(TableExtractionTemplateCreateRoute.routeDescriptor.path, {
          modelId,
          tableId,
        })
      );
  };

  const handleEditClick = () => {
    tableId &&
      history.push(
        generatePath(ModelTableEditRoute.routeDescriptor.path, {
          modelId,
          tableId,
        })
      );
  };

  const onSubmit = async (data: TargetTableFormValues) => {
    try {
      const tableData = {
        targetTableDefinitionName:
          mode === TableMode.Create
            ? toLowerCaseKabobCaseAlphaNumeric(data.tableName)
            : targetTableDetails?.name ?? '',
        displayName: data.tableName,
        description: data.description,
        dsp: data.dsp
          ? {
              submissionType: data.submissionType,
              orgSlug: orgSlug,
            }
          : null,
        columns: data.tableData.map((column) => {
          const mappingFields = mappingFieldsData?.filter(
            (field) => field.fieldName === column.name
          );
          const tsaFields = tsaFieldsData?.find(
            (field) => field.fieldName === column.name
          );
          const tsa = {
            required: tsaFields
              ? tsaFields?.tsa === TSA_VALUES.REQUIRED
              : false,
            enabled: tsaFields ? tsaFields?.tsa !== TSA_VALUES.DISABLED : false,
          };
          return {
            ...column,
            dsp: data.dsp
              ? {
                  ...column.dsp,
                  defaultValue: column.dsp?.defaultValue?.trim() || null,
                  required: column.dsp?.required ?? false,
                  mappingFields:
                    mappingFields?.map((mappingField) => ({
                      mappingName: mappingField.mappingName ?? '',
                      mappingValue: mappingField.mappingValue ?? '',
                      targetName: mappingField.targetName ?? '',
                      displayName: mappingField.displayName ?? '',
                      descriptionName: mappingField.descriptionName ?? '',
                      displayPreference: mappingField.displayPreference ?? '',
                      required: !!mappingField.required,
                      tsa,
                    })) || null,
                  tsa,
                }
              : null,
          };
        }),
      };
      if (mode === TableMode.Create) {
        await createTable(tableData);
      } else if (mode === TableMode.Edit) {
        await updateTable(tableData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Determine page title based on mode
  const pageTitle =
    mode === TableMode.Create
      ? 'Custom Data Model'
      : targetTableDetails?.displayName || targetTableDetails?.name || '';

  return (
    <PageContainer id='TargetTablePage'>
      <Box display='flex' flexDirection='column' paddingY={'10px'} gap='20px'>
        <Panel id='TargetTableBreadcrumbs' behavior='fallback'>
          <LibraryBreadcrumbs
            modelName={
              modelDetailsFromTable?.displayName ?? modelDetails?.displayName
            }
            tableName={
              mode !== TableMode.Create
                ? targetTableDetails?.displayName ?? targetTableDetails?.name
                : undefined
            }
          />
        </Panel>
        <Panel id='TargetTableHeader' behavior='fallback'>
          {mode === TableMode.Create ? (
            <Typography variant='h6'>{pageTitle}</Typography>
          ) : (
            <Header
              title={pageTitle}
              isDsp={dsp}
              editCallback={
                mode === TableMode.Read ? handleEditClick : undefined
              }
            />
          )}
        </Panel>

        {mode === TableMode.Read && targetTableDetails?.dsp?.orgSlug && (
          <Panel id='TableDetailsOrganization' behavior='fallback'>
            <Paper
              sx={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}
            >
              <Box display='flex' flexDirection='column' gap='8px'>
                <Typography variant='h6'>Organizations</Typography>
                <Typography variant='body1'>
                  This Target Table is assigned to the below organization.
                </Typography>
                <Box flexGrow={1} />
                <TextField
                  defaultValue={targetTableDetails?.dsp?.orgSlug}
                  color='primary'
                  placeholder='Organization'
                  size='small'
                  disabled
                />
              </Box>
            </Paper>
          </Panel>
        )}

        {mode === TableMode.Read && tableId && (
          <Panel id='TableDetailsTemplates' behavior='fallback'>
            <TemplatesSectionWrapper
              handleCreateClick={handleCreateTemplateClick}
            />
          </Panel>
        )}
        {(mode === TableMode.Create || mode === TableMode.Edit) && (
          <Panel id='TargetTableDetails' behavior='fallback'>
            <Paper
              sx={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}
            >
              <Typography variant='h6'>Define your model</Typography>
              <Grid container spacing={3}>
                <Grid xs={12} md={6}>
                  <Box
                    display='flex'
                    flexDirection='column'
                    gap='10px'
                    height='100%'
                  >
                    <Typography variant='h6'>1. Target Table Name</Typography>
                    <Typography variant='body1'>
                      {`You're ${
                        mode === TableMode.Create ? 'creating' : 'viewing'
                      } a target table that's not part of our
                      standard library. ${
                        mode === TableMode.Create
                          ? 'To start, what would you like to name this table?'
                          : ''
                      }`}
                    </Typography>
                    <Box flexGrow={1} />
                    <ControlledInput
                      size='small'
                      fullWidth
                      placeholder='Enter target table name'
                      control={tableForm.control}
                      name='tableName'
                      error={!!tableForm.errors.tableName}
                      helperText={tableForm.errors.tableName?.message}
                    />
                  </Box>
                </Grid>
                <Grid xs={12} md={6}>
                  <Box
                    display='flex'
                    flexDirection='column'
                    gap='10px'
                    height='100%'
                  >
                    <Typography variant='h6'>
                      2. Target Table Description (Optional)
                    </Typography>
                    <Typography variant='body1'>
                      {mode === TableMode.Create
                        ? 'Add a short description about this target table.'
                        : 'Description for this target table.'}
                    </Typography>
                    <Box flexGrow={1} />

                    <ControlledInput
                      control={tableForm.control}
                      name='description'
                      placeholder='Enter target table description'
                      size='small'
                      fullWidth
                      error={!!tableForm.errors.description}
                      multiline
                      maxRows={4}
                    />
                  </Box>
                </Grid>
                <Grid xs={12} md={6}>
                  <Box
                    display='flex'
                    flexDirection='column'
                    gap='10px'
                    height='100%'
                  >
                    <Typography variant='h6'>
                      3. Data Submission Portal
                    </Typography>
                    <Typography variant='body1'>
                      Is this a target table for the data submission portal?
                      {mode === TableMode.Create &&
                        ' Checking this box will allow you to configure columns specifically for the data submission portal.'}
                    </Typography>
                    <Box flexGrow={1} />
                    {mode === TableMode.Edit ? (
                      <Tooltip
                        title={
                          'A target table cannot be converted to a data submission portal target table after it has been created and vice versa.'
                        }
                        arrow
                        placement='top-start'
                      >
                        <span>
                          <ControlledCheckbox
                            control={tableForm.control}
                            name='dsp'
                            disabled
                            label='For use within the Data Submission Portal'
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <ControlledCheckbox
                        control={tableForm.control}
                        name='dsp'
                        label='For use within the Data Submission Portal'
                      />
                    )}
                  </Box>
                </Grid>
                {dsp ? (
                  <Grid xs={12} md={6}>
                    <Box
                      display='flex'
                      flexDirection='column'
                      gap='10px'
                      height='100%'
                    >
                      <Typography variant='h6'>
                        4. Data Submission Portal - Submission Type
                      </Typography>
                      <Typography variant='body1'>
                        What type of target table does this represent with the
                        data submission portal? Ex. Financial or Operational
                      </Typography>
                      <Box flexGrow={1} />
                      <Controller
                        name='submissionType'
                        control={tableForm.control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            fullWidth
                            options={['financial', 'operational']}
                            getOptionLabel={(option) => capitalize(option)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                color='primary'
                                size='small'
                                placeholder='Select submission type'
                                error={!!tableForm.errors['submissionType']}
                                helperText={
                                  tableForm.errors['submissionType']?.message
                                }
                              />
                            )}
                            onChange={(_, value) => field.onChange(value)}
                            disabled={mode === TableMode.Edit} // Submission type always disabled in edit mode
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                ) : null}
              </Grid>
            </Paper>
          </Panel>
        )}
        <Panel id='TargetTableFields' behavior='fallback'>
          <Box display='flex' flexDirection='column' gap='20px'>
            <Paper
              sx={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}
            >
              <Box
                display='flex'
                flexDirection='row'
                justifyContent='space-between'
              >
                <Box display='flex' flexDirection='column' gap='10px'>
                  <Typography variant='h6'>{`${dsp ? 5 : 4}. ${
                    mode === TableMode.Create ? 'Add' : 'View'
                  } target fields`}</Typography>
                  <Typography variant='body1'>
                    {mode === TableMode.Create
                      ? `Add target fields below and define each field's data type or
                         upload a CSV with the columns below to get started.`
                      : 'Below are the target fields for this table.'}
                  </Typography>
                </Box>
                {mode === TableMode.Create && (
                  <Box
                    display='flex'
                    flexDirection='column'
                    justifyContent={'center'}
                  >
                    <Button
                      size='small'
                      variant='contained'
                      endIcon={<CloudUploadIcon />}
                      onClick={handleFileUploadClick}
                    >
                      Upload File
                    </Button>
                  </Box>
                )}
              </Box>

              {tableForm.errors.tableData?.root?.message && (
                <Alert severity='error'>{`Table Schema: ${tableForm.errors.tableData?.root?.message}`}</Alert>
              )}

              <Tabs
                value={tabValue}
                onChange={(event, newValue) => setTabValue(newValue)}
                aria-label='Target Table tabs'
                showBorder={false}
                muiTabsProps={{
                  TabIndicatorProps: {
                    sx: {
                      top: 0,
                    },
                  },
                }}
                tabs={[
                  {
                    label: 'Table Schema',
                    icon: <DataConnectionIcon />,
                    iconPosition: 'start',
                    value: TabValue.TableSchema,
                  },
                  ...(dsp
                    ? [
                        {
                          label: 'Mapping Configurations',
                          icon: <DataSettingsIcon />,
                          iconPosition: 'start' as const,
                          value: TabValue.MappingConfig,
                        },
                        {
                          label: 'Product Configurations',
                          icon: <SettingsIcon />,
                          iconPosition: 'start' as const,
                          value: TabValue.ProductConfig,
                        },
                      ]
                    : []),
                ]}
                tabPanels={[
                  {
                    value: TabValue.TableSchema,
                    panel: (
                      <SchemaTable
                        control={tableForm.control}
                        handleFieldNameBlur={tableForm.handleFieldNameBlur}
                        tableMode={mode}
                        targetTableState={tableData}
                        setTargetTableState={setTableData}
                        dsp={dsp}
                      />
                    ),
                  },
                  ...(dsp
                    ? [
                        {
                          value: TabValue.MappingConfig,
                          panel: (
                            <Box
                              display='flex'
                              flexDirection='column'
                              gap='20px'
                            >
                              <Box
                                display='flex'
                                flexDirection='column'
                                gap='8px'
                              >
                                <Typography variant='h6'>
                                  Configure mapped fields below
                                </Typography>
                                <Typography variant='body1'>
                                  From the created table schema, are there any
                                  fields that should have corresponding mapped
                                  values (e.g. the values are mapped from a
                                  source value to a target value)? If so, add
                                  those fields below.
                                </Typography>
                              </Box>
                              <DspMappingTable
                                tableMode={mode}
                                fieldNameOptions={fieldNameOptions}
                                getMappingValues={mappingForm.getMappingValues}
                                setMappingValues={mappingForm.setMappingValue}
                              />
                            </Box>
                          ),
                        },
                        {
                          value: TabValue.ProductConfig,
                          panel: (
                            <Box
                              display='flex'
                              flexDirection='column'
                              gap='20px'
                            >
                              <Typography variant='h6'>
                                Data Submission Portal - Topside Adjustments
                              </Typography>
                              <DspConfigurationTable
                                control={tsaForm.control}
                                tableMode={mode}
                                targetTableState={tsaFieldsData}
                                setTargetTableState={setTsaTableData}
                                fieldNameOptions={fieldNameOptions}
                              />
                            </Box>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
              {mode === TableMode.Read && (
                <Box display='flex' flexDirection='row'>
                  <Button
                    startIcon={<CopyIcon />}
                    onClick={handleDuplicateTableClick}
                  >
                    Duplicate Target Table
                  </Button>
                </Box>
              )}
            </Paper>
            <Box
              display='flex'
              flexDirection='row'
              justifyContent='end'
              alignItems='center'
              gap='20px'
            >
              {mode === TableMode.Create || mode === TableMode.Edit ? (
                <>
                  <Button
                    onClick={() =>
                      history.push(
                        generatePath(ModelDetailsRoute.routeDescriptor.path, {
                          modelId,
                        })
                      )
                    }
                    type='button'
                  >
                    Back
                  </Button>
                  <Button
                    disabled={createdTable}
                    variant='contained'
                    onClick={() => {
                      tableForm.handleSubmit(onSubmit, (errors) =>
                        console.log('ERRORS:', errors)
                      )();
                    }}
                  >
                    Save
                  </Button>
                </>
              ) : null}
            </Box>
          </Box>
        </Panel>
      </Box>
    </PageContainer>
  );
};

TargetTableCreate.displayName = 'TargetTableCreate';
export default TargetTableCreate;
